<script setup>
import { ref, computed, nextTick } from 'vue';

const props = defineProps({
  imageUrl: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Object,
    default: () => ({ x: 0.5, y: 0.5 }),
  },
  fileName: {
    type: String,
    default: '',
  },
  alt: {
    type: String,
    default: '',
  },
  caption: {
    type: String,
    default: '',
  },
  altRequired: {
    type: Boolean,
    default: false,
  },
  altError: {
    type: String,
    default: '',
  },
  // To keep the IDs unique for alt text/caption
  idPrefix: {
    type: String,
    default: 'focal-point',
  },
});

const fitToContent = el => {
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
};

const vAutoResize = {
  mounted(el) {
    el.__autoResize = () => fitToContent(el);
    el.addEventListener('input', el.__autoResize);
    nextTick(() => fitToContent(el));
  },
  updated(el) {
    fitToContent(el);
  },
  beforeUnmount(el) {
    el.removeEventListener('input', el.__autoResize);
  },
};

const point = computed(() => props.modelValue ?? { x: 0.5, y: 0.5 });

const emit = defineEmits(['update:modelValue', 'remove', 'update:alt', 'update:caption']);

const active = ref(false);
const imageRef = ref(null);
const canvasRef = ref(null);

const FOCAL_STEP = 0.01;
const FOCAL_STEP_LARGE = 0.05;

const positionLabel = computed(
  () =>
    `Focal point ${Math.round(point.value.x * 100)}% from left, ${Math.round(
      point.value.y * 100
    )}% from top`
);

const handleCanvasClick = event => {
  if (!active.value) return;
  const rect = imageRef.value.getBoundingClientRect();
  const x = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
  const y = Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height));
  emit('update:modelValue', { x, y });
};

const nudge = (axis, delta) => {
  emit('update:modelValue', {
    ...point.value,
    [axis]: Math.max(0, Math.min(1, point.value[axis] + delta)),
  });
};

const handleCanvasKeydown = event => {
  if (!active.value) return;

  const step = event.shiftKey ? FOCAL_STEP_LARGE : FOCAL_STEP;
  const moves = {
    ArrowLeft: () => nudge('x', -step),
    ArrowRight: () => nudge('x', step),
    ArrowUp: () => nudge('y', -step),
    ArrowDown: () => nudge('y', step),
  };

  const move = moves[event.key];
  if (move) {
    event.preventDefault();
    move();
  }
};

const toggleActive = () => {
  active.value = !active.value;
  if (active.value) {
    nextTick(() => canvasRef.value?.focus());
  }
};
</script>

<template>
  <div class="focal-point-picker">
    <div class="focal-point-picker__media">
      <div class="focal-point-picker__image-block">
        <div
          ref="canvasRef"
          class="focal-point-picker__canvas"
          :class="{ 'focal-point-picker__canvas--active': active }"
          :tabindex="active ? 0 : undefined"
          :role="active ? 'application' : undefined"
          :aria-label="
            active
              ? `${positionLabel}. Use the arrow keys to move it, holding Shift for larger steps.`
              : undefined
          "
          @click="handleCanvasClick"
          @keydown="handleCanvasKeydown"
        >
          <img
            ref="imageRef"
            :src="imageUrl"
            alt="Featured media preview"
            class="focal-point-picker__image"
          />
          <div
            v-if="active"
            class="focal-point-picker__marker"
            :style="{ left: `${point.x * 100}%`, top: `${point.y * 100}%` }"
          />
        </div>
        <p v-if="fileName" class="focal-point-picker__filename">{{ fileName }}</p>
        <p v-if="active" class="focal-point-picker__sr-only" aria-live="polite">
          {{ positionLabel }}
        </p>
      </div>

      <div class="focal-point-picker__meta">
        <div class="focal-point-picker__field">
          <label :for="`${idPrefix}-alt`" class="focal-point-picker__label">
            Alt text
            <span v-if="altRequired" class="focal-point-picker__required">*</span>
          </label>
          <textarea
            :id="`${idPrefix}-alt`"
            v-auto-resize
            :value="alt"
            class="focal-point-picker__textarea"
            :class="{ 'focal-point-picker__textarea--error': altError }"
            rows="1"
            :required="altRequired"
            :aria-invalid="altError ? 'true' : undefined"
            :aria-describedby="altError ? `${idPrefix}-alt-error` : undefined"
            @input="$emit('update:alt', $event.target.value)"
          />
          <p
            v-if="altError"
            :id="`${idPrefix}-alt-error`"
            class="focal-point-picker__error"
            role="alert"
          >
            {{ altError }}
          </p>
        </div>
        <div class="focal-point-picker__field">
          <label :for="`${idPrefix}-caption`" class="focal-point-picker__label">Caption</label>
          <textarea
            :id="`${idPrefix}-caption`"
            v-auto-resize
            :value="caption"
            class="focal-point-picker__textarea"
            rows="1"
            @input="$emit('update:caption', $event.target.value)"
          />
        </div>
      </div>
    </div>

    <div class="focal-point-picker__actions">
      <button
        type="button"
        class="focal-point-picker__toggle"
        :class="{ 'focal-point-picker__toggle--active': active }"
        @click="toggleActive"
      >
        {{ active ? 'Close focal point picker' : 'Set focal point' }}
      </button>
      <button type="button" class="focal-point-picker__remove" @click="$emit('remove')">
        Remove image
      </button>
    </div>
    <p v-if="active" class="focal-point-picker__hint">
      Click the image or use the arrow keys to set the focal point of the image.
    </p>
  </div>
</template>

<style scoped>
.focal-point-picker__canvas:focus-visible {
  outline: 2px solid var(--gsd-red-base, #c00);
  outline-offset: 2px;
}

.focal-point-picker__sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
